<script lang="ts" setup>
import type { IDataItem } from "./data"
import type { MessageRecordType } from "@/api/business/types/message"
import type { MerchantOmsCustomerReservation } from "@/api/business/types/table"
import { Bell } from "@element-plus/icons-vue"
import dayjs from "dayjs"
import { ElMessage, ElNotification } from "element-plus"
import { computed, h, onBeforeUnmount, ref, watch } from "vue"
import { getMessageListByShopId, getUnreadMessageListByShopId } from "@/api/business/message"
import { MessageStatusConstants, MessageTypeConstants } from "@/api/business/types/message"
import {
  dispatchSensitiveApprovalChanged,
  shouldRefreshTableAfterSensitiveApproval,
} from "@/features/sensitive-approval/approvalPending"
import {
  buildSensitiveApprovalNotification,
  createSensitiveApprovalPendingPromptTracker,
  parseSensitiveApprovalMessage,
  shouldShowSensitiveApprovalNotification,
} from "@/features/sensitive-approval/approvalWebsocket"
import SensitiveApprovalReviewDrawer from "@/features/sensitive-approval/SensitiveApprovalReviewDrawer.vue"
import { useSensitiveApprovalStore } from "@/store/modules/sensitiveApproval"
import { useTableStore } from "@/store/modules/table"
import { useUserStore } from "@/store/modules/user"
import { useViewScopeStore } from "@/store/modules/viewScope"
import { useWsStore } from "@/store/modules/wsStore"
import { MessageType } from "@/types/messageType"
import { shouldSuppressPackageCountdownReminderMessage } from "@/utils/packageCountdownReminder"
import { resolveActiveShopId } from "@/utils/shopScope"
import { getMessageContent } from "./messageHandler"
import { getRealtimeNotificationDuration } from "./notificationDuration"
import { clearNotificationSpeechQueue, enqueueNotificationSpeech } from "./notificationSpeechQueue"
import NotifyList from "./NotifyList.vue"
import { shouldDisplayReservationExpiredNotification } from "./reservationExpiredNotificationDedup"
import ReserveNotify from "./ReserveNotification.vue"

const badgeMax = 99
const useWs = useWsStore()
const useUser = useUserStore()
const viewScopeStore = useViewScopeStore()
const tableStore = useTableStore()
const sensitiveApprovalStore = useSensitiveApprovalStore()
const activeShopId = computed(() => resolveActiveShopId({
  activeScope: viewScopeStore.activeScope,
  userShopId: useUser.shopId,
}))
const current = ref(1)
const size = ref(50)
const messageList = ref<MessageRecordType[]>([])
const unreadMessageListTotal = ref(0)
const drawerVisible = ref<boolean>(false)
const reviewDrawerVisible = ref(false)
const reviewTicketId = ref<number>()
const sensitiveApprovalPendingPromptTracker = createSensitiveApprovalPendingPromptTracker()
const {
  clearTableCategoryListV2Cache,
  enhancedDebounceRefresh,
} = tableStore
onBeforeUnmount(() => {
  clearNotificationSpeechQueue()
})

watch(
  () => sensitiveApprovalStore.requestedReviewTicketId,
  (ticketId) => {
    if (!ticketId) {
      return
    }
    reviewTicketId.value = ticketId
    reviewDrawerVisible.value = true
    sensitiveApprovalStore.consumeReviewRequest(ticketId)
  },
  { immediate: true },
)

function getVisibleMessageRecords(records: MessageRecordType[] = []) {
  const tableList = tableStore.getOpeningTableList()
  return records.filter(record => !shouldSuppressPackageCountdownReminderMessage(record, tableList))
}

const _data = computed(() => {
  const list: IDataItem[] = [
    {
      name: "服务呼叫",
      type: "primary",
      typeEnum: [4],
      list: [],
    },
    {
      name: "桌台消息",
      type: "success",
      typeEnum: [1, 3, 5, 10],
      list: [],
    },
    {
      name: "库存预警",
      type: "warning",
      typeEnum: [2],
      list: [],
    },
  ]
  messageList.value.forEach((item) => {
    const index = list.findIndex((i) => i.typeEnum.includes(item.messageType))
    if (index !== -1) {
      list[index].list.push({
        messasgeId: item.merchantMessageRecordId as number,
        title: item.messageTitle,
        description: item.messageContent,
        datetime: dayjs(item.createTime).format("YYYY-MM-DD HH:mm:ss"),
        messageStatus: item.messageStatus,
      })
    }
  })
  return list
})

watch(
  () => useWs.getData(),
  (data: any) => {
    if (!data.includes("pong") && !data.includes("ping")) {
      const message = JSON.parse(data)
      if (message.type === MessageType.Reservation) {
        const tableReservationInfo = JSON.parse(message.data) as MerchantOmsCustomerReservation
        if (typeof tableReservationInfo.reserveTableCategoryId === "string") {
          tableReservationInfo.reserveTableCategoryId = JSON.parse(tableReservationInfo.reserveTableCategoryId)
        }
        const notification = ElNotification({
          title: "预约待确认",
          type: "warning",
          dangerouslyUseHTMLString: true,
          message: h(ReserveNotify, { tableReservationInfo, onClose: () => {
            notification.close()
          } }),
          position: "top-left",
          duration: 0,
        })
        return
      }
      if (message.type === MessageType.Message || message.type === MessageType.PreCheckoutReminder || message.type === MessageType.PackageCountdownReminder) {
        const content = JSON.parse(message.data) as MessageRecordType
        if (shouldSuppressPackageCountdownReminderMessage(content, tableStore.getOpeningTableList())) {
          getAllMessageList()
          return
        }
        const messageContent = getMessageContent(content)
        if (shouldDisplayReservationExpiredNotification(content)) {
          ElNotification({
            title: content.messageTitle,
            message: messageContent,
            type: "warning",
            duration: getRealtimeNotificationDuration(message.type, content),
          })
          // handleSpeak(`${content.messageTitle},${messageContent}`)
          enqueueNotificationSpeech(`${content.messageTitle},${messageContent}`)
        }
        getAllMessageList()
      }
      if (message.type === MessageType.CreditExchangeNotification) {
        const content = {
          messageType: MessageTypeConstants.MESSAGE_TYPE_CREDIT_EXCHANGE_NOTIFICATION,
          messageTitle: '积分兑换',
          messageContent: message.data,
          shopId: activeShopId.value ?? 0,
          messageStatus: MessageStatusConstants.NOT_PROCESSED,
          messageHandler: useUser.getUserInfo()!.corpUserId!,
        }
        const messageContent = getMessageContent(content)
        ElNotification({
          title: content.messageTitle,
          message: messageContent,
          type: "warning",
          duration: 0,
        })
        // handleSpeak(`${content.messageTitle},${messageContent}`)
        enqueueNotificationSpeech(`${content.messageTitle},${messageContent}`)
        getAllMessageList()
      }
      if (message.type === MessageType.SensitiveApproval) {
        const payload = parseSensitiveApprovalMessage(message)
        if (payload) {
          sensitiveApprovalStore.upsertFromPayload(payload)
          if (shouldShowSensitiveApprovalNotification(payload)) {
            const notification = buildSensitiveApprovalNotification(payload)
            ElNotification({
              title: notification.title,
              message: notification.message,
              type: notification.type,
              duration: payload.status === 'EXECUTE_FAILED' ? 0 : 4500,
            })
          }
          if (sensitiveApprovalPendingPromptTracker.shouldPrompt(payload, useUser.getUserInfo())) {
            reviewTicketId.value = payload.ticketId
            reviewDrawerVisible.value = true
          }
          if (shouldRefreshTableAfterSensitiveApproval(payload)) {
            enhancedDebounceRefresh()
          }
          dispatchSensitiveApprovalChanged(payload)
        }
      }
      if (message.type === MessageType.RefreshMessage) {
        getAllMessageList()
      }
      if (message.type === MessageType.RefreshProduct) {
        tableStore.fetchBaseCategoryAndProductList()
      }
      if (message.type === MessageType.RefreshTableCategory) {
        clearTableCategoryListV2Cache()
        enhancedDebounceRefresh()
      }
    }
  },
)

const clearMessageList = () => {
  messageList.value = []
  unreadMessageListTotal.value = 0
}

const getAllMessageList = () => {
  const shopId = activeShopId.value
  if (!shopId) {
    clearMessageList()
    return
  }
  Promise.all([getUnreadMessageListByShopId(shopId, current.value, size.value), getMessageListByShopId(shopId, current.value, size.value)])
    .then(([unreadRes, res]) => {
      if (unreadRes.code === 0 && res.code === 0) {
        const visibleMessages = getVisibleMessageRecords(res.data.records)
        const visibleUnreadMessages = getVisibleMessageRecords(unreadRes.data.records)
        messageList.value = visibleMessages
        unreadMessageListTotal.value = Math.max(0, unreadRes.data.total - (unreadRes.data.records.length - visibleUnreadMessages.length))
      } else {
        ElMessage.error(res.msg)
      }
    })
}

watch(activeShopId, () => {
  getAllMessageList()
}, { immediate: true })

const handleShowNoify = () => {
  drawerVisible.value = true
}
</script>

<template>
  <div class="notify">
    <el-badge style="height: 20px" :value="unreadMessageListTotal" :max="badgeMax" :hidden="unreadMessageListTotal === 0">
      <div @click="handleShowNoify" class="flex items-center justify-center">
        <el-icon :size="20" class="flex-shrink-0">
          <Bell />
        </el-icon>
        <span class="ml-4px leading-none">消息</span>
      </div>
    </el-badge>

    <NotifyList v-model:visible="drawerVisible" @refresh="getAllMessageList" :message-list="messageList" />
    <SensitiveApprovalReviewDrawer
      v-model="reviewDrawerVisible"
      :ticket-id="reviewTicketId"
      @handled="getAllMessageList"
    />
  </div>
</template>

<style lang="scss" scoped>
.notify {
  margin-right: 10px;
  // color: var(--el-text-color-regular);
  color: #fff;
}

.message-tabs {
  &:deep(.el-scrollbar__thumb) {
    display: none;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.notify-history {
  padding-top: 12px;
  text-align: center;
  border-top: 1px solid var(--el-border-color);
}
</style>
